import { useCallback, useEffect, useState } from 'react';

type SessionEntry = {
  key: string;
  lastActivity?: string;
  messageCount?: number;
};

type PriorityItem = {
  id: string;
  title: string;
  priority: 'p0' | 'p1' | 'p2' | 'p3';
  status: 'active' | 'blocked' | 'pending';
};

type AgentStatus = {
  connected: boolean;
  authOk: boolean;
  gatewayVersion?: string;
  error?: string;
};

const POLL_MS = 30_000;

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!response.ok) throw new Error(`Request failed (${response.status})`);
  return response.json();
}

export function useSessionList(organizationId?: string, enabled = false) {
  const [sessions, setSessions] = useState<SessionEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled || !organizationId) return;
    setIsLoading(true);
    setError(null);
    try {
      const payload = await fetchJson<any>('/api/v1/agent/gateway/tools/invoke', {
        method: 'POST',
        body: JSON.stringify({
          organization: organizationId,
          toolName: 'sessions_list',
          arguments: {},
        }),
      });
      const result = payload?.data?.result ?? payload?.result ?? payload?.data;
      const list = Array.isArray(result?.sessions) ? result.sessions : [];
      setSessions(
        list.map((item: any) => ({
          key: String(item.key ?? item.sessionKey ?? 'unknown'),
          lastActivity: item.lastActivity ?? item.updatedAt,
          messageCount: Number(item.messageCount ?? 0),
        })),
      );
    } catch (err: any) {
      setError(err.message || 'Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, organizationId]);

  useEffect(() => {
    if (!enabled) return;
    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, load]);

  return { sessions, isLoading, error, refresh: load };
}

export function usePriorityStack(organizationId?: string, enabled = false) {
  const [priorities, setPriorities] = useState<PriorityItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled || !organizationId) return;
    setIsLoading(true);
    setError(null);
    try {
      const payload = await fetchJson<any>('/api/v1/agent/gateway/tools/invoke', {
        method: 'POST',
        body: JSON.stringify({
          organization: organizationId,
          toolName: 'task_stack',
          arguments: {},
        }),
      });
      const result = payload?.data?.result ?? payload?.result ?? payload?.data;
      const list = Array.isArray(result?.items) ? result.items : [];
      setPriorities(
        list.map((item: any, idx: number) => ({
          id: String(item.id ?? `priority-${idx}`),
          title: String(item.title ?? item.task ?? 'Untitled task'),
          priority: (item.priority ?? 'p2') as PriorityItem['priority'],
          status: (item.status ?? 'pending') as PriorityItem['status'],
        })),
      );
    } catch (err: any) {
      setError(err.message || 'Failed to load priorities');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, organizationId]);

  useEffect(() => {
    if (!enabled) return;
    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, load]);

  return { priorities, isLoading, error, refresh: load };
}

export function useAgentStatus(organizationId?: string, enabled = false) {
  const [status, setStatus] = useState<AgentStatus>({ connected: false, authOk: false });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled || !organizationId) return;
    setIsLoading(true);
    setError(null);
    try {
      const url = `/api/v1/organization/${organizationId}/agent/gateway/status`;
      const data = await fetchJson<AgentStatus>(url);
      setStatus(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load status');
      setStatus({ connected: false, authOk: false, error: err.message });
    } finally {
      setIsLoading(false);
    }
  }, [enabled, organizationId]);

  useEffect(() => {
    if (!enabled) return;
    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, load]);

  return { status, isLoading, error, refresh: load };
}
